// src/pages/ViewProducts.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './ProductInventoryView.module.css';
import { getProducts } from '../api/productApi';
import AppLayout from '../components/AppLayout';
import ProductImage from '../components/ProductImage';

const translations = {
  en: {
    pageTitle: 'Product Inventory',
    searchPlaceholder: 'Search by SKU...',
    noImage: 'No Image',
    noProductsMessage: 'No products match the current filters.',
    sku: 'SKU',
    size: 'Size',
	    price: 'Price',
	    totalQty: 'Available total',
	    totalWithHold: 'Total with hold',
	    totalHeld: 'Held total',
	    inventoryValue: 'Available stock value',
	    inventoryValueWithHold: 'Stock value with hold',
	    availableValue: 'Available value',
	    heldValue: 'Held value',
	    sortsAndColors: 'Sorts & Colors',
	    sortLabel: 'Sort',
	    available: 'Available',
	    held: 'Held',
    showDetails: 'Show Details',
    hideDetails: 'Hide Details',
    showColors: 'Show Colors',
    hideColors: 'Hide Colors',
    failedFetch: 'Failed to fetch products. Please try again.',
    loading: 'Loading...',
    loadingImage: 'Loading image...',
    imageUnavailable: 'Image unavailable',
    homepage: 'Homepage',
    reset: 'Reset Filters',
    close: 'Close',
  },
  ar: {
    pageTitle: 'مخزون المنتجات',
    searchPlaceholder: 'ابحث برمز المنتج (SKU)...',
    noImage: 'لا توجد صورة',
    noProductsMessage: 'لا توجد منتجات مطابقة للفلتر الحالي.',
    sku: 'رمز المنتج (SKU)',
    size: 'الحجم',
	    price: 'السعر',
	    totalQty: 'المتاح',
	    totalWithHold: 'الإجمالي مع المحجوز',
	    totalHeld: 'إجمالي المحجوز',
	    inventoryValue: 'قيمة المخزون المتاح',
	    inventoryValueWithHold: 'القيمة مع المحجوز',
	    availableValue: 'قيمة المتاح',
	    heldValue: 'قيمة المحجوز',
	    sortsAndColors: 'الأصناف والألوان',
	    sortLabel: 'الصنف',
	    available: 'المتاح',
	    held: 'محجوز',
    showDetails: 'عرض التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    showColors: 'عرض الألوان',
    hideColors: 'إخفاء الألوان',
    failedFetch: 'فشل جلب المنتجات. حاول مرة أخرى.',
    loading: 'جارٍ التحميل...',
    loadingImage: 'جارٍ تحميل الصورة...',
    imageUnavailable: 'الصورة غير متاحة',
    homepage: 'الصفحة الرئيسية',
    reset: 'إعادة التصفية',
    close: 'إغلاق',
  },
};

const ProductInventoryView = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  const [products, setProducts] = useState([]);
  const [searchSKU, setSearchSKU] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedSorts, setExpandedSorts] = useState({});

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await getProducts();
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        setErrorMessage(t('failedFetch'));
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProducts = useMemo(() => {
    if (!searchSKU.trim()) return products;
    return products.filter((product) =>
      product.SKU.toLowerCase().includes(searchSKU.toLowerCase())
    );
  }, [products, searchSKU]);

  const resetFilters = () => {
    setSearchSKU('');
  };

  const toggleProduct = (productId) => {
    setExpandedProducts((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleSort = (sortKey) => {
    setExpandedSorts((prev) => ({ ...prev, [sortKey]: !prev[sortKey] }));
  };

  const getSortTotals = useCallback((sortObj) => {
    const colors = Array.isArray(sortObj.colors) ? sortObj.colors : [];
    const price = Number(sortObj.price) || 0;
    const available = colors.reduce((acc, color) => acc + (Number(color.quantity) || 0), 0);
    const held = colors.reduce((acc, color) => acc + (Number(color.reservedQuantity) || 0), 0);
    return {
      available,
      held,
      totalWithHold: available + held,
      availableValue: available * price,
      heldValue: held * price,
      totalWithHoldValue: (available + held) * price,
    };
  }, []);

  const getProductTotals = useCallback((product) => {
    const sorts = Array.isArray(product.sorts) ? product.sorts : [];
    return sorts.reduce(
      (totals, sort) => {
        const sortTotals = getSortTotals(sort);
        return {
          available: totals.available + sortTotals.available,
          held: totals.held + sortTotals.held,
          totalWithHold: totals.totalWithHold + sortTotals.totalWithHold,
          availableValue: totals.availableValue + sortTotals.availableValue,
          heldValue: totals.heldValue + sortTotals.heldValue,
          totalWithHoldValue: totals.totalWithHoldValue + sortTotals.totalWithHoldValue,
        };
      },
      {
        available: 0,
        held: 0,
        totalWithHold: 0,
        availableValue: 0,
        heldValue: 0,
        totalWithHoldValue: 0,
      }
    );
  }, [getSortTotals]);

  const inventoryTotals = useMemo(
    () =>
      filteredProducts.reduce(
        (totals, product) => {
          const productTotals = getProductTotals(product);
          return {
            available: totals.available + productTotals.available,
            held: totals.held + productTotals.held,
            totalWithHold: totals.totalWithHold + productTotals.totalWithHold,
            availableValue: totals.availableValue + productTotals.availableValue,
            heldValue: totals.heldValue + productTotals.heldValue,
            totalWithHoldValue: totals.totalWithHoldValue + productTotals.totalWithHoldValue,
          };
        },
        {
          available: 0,
          held: 0,
          totalWithHold: 0,
          availableValue: 0,
          heldValue: 0,
          totalWithHoldValue: 0,
        }
      ),
    [filteredProducts, getProductTotals]
  );

  const canViewPrices = ['admin', 'manager', 'sales', 'sales-manager', 'accounting'].includes(
    userInfo.userType
  );

  const formatPrice = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 2,
      }).format(numericValue);
    } catch (error) {
      return `${numericValue.toFixed(2)} EGP`;
    }
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.inventoryPage}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p>{t('loading')}</p>
        </div>
      )}

      {!loading && (
        <>
          <section className={styles.filterBar}>
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchSKU}
              onChange={(e) => setSearchSKU(e.target.value)}
              className={styles.searchInput}
            />
            <button className={styles.resetButton} onClick={resetFilters} disabled={!searchSKU}>
              {t('reset')}
            </button>
          </section>

          {errorMessage && (
            <div className={`${styles.alert} ${styles.alertError}`} role="alert">
              <span>{errorMessage}</span>
              <button type="button" className={styles.alertDismiss} onClick={() => setErrorMessage(null)}>
                {t('close')}
              </button>
            </div>
          )}

          <section className={styles.inventorySummary}>
            <div className={styles.summaryMetric}>
              <span>{t('totalQty')}</span>
              <strong>{inventoryTotals.available}</strong>
            </div>
            <div className={styles.summaryMetric}>
              <span>{t('totalHeld')}</span>
              <strong>{inventoryTotals.held}</strong>
            </div>
            <div className={styles.summaryMetric}>
              <span>{t('totalWithHold')}</span>
              <strong>{inventoryTotals.totalWithHold}</strong>
            </div>
            {canViewPrices && (
              <>
                <div className={styles.summaryMetric}>
                  <span>{t('inventoryValue')}</span>
                  <strong>{formatPrice(inventoryTotals.availableValue)}</strong>
                </div>
                <div className={styles.summaryMetric}>
                  <span>{t('inventoryValueWithHold')}</span>
                  <strong>{formatPrice(inventoryTotals.totalWithHoldValue)}</strong>
                </div>
              </>
            )}
          </section>

          {filteredProducts.length === 0 ? (
            <div className={styles.emptyState}>{t('noProductsMessage')}</div>
          ) : (
            <section className={styles.productGrid}>
              {filteredProducts.map((product) => {
                const productTotals = getProductTotals(product);

                return (
                  <article key={product._id} className={styles.productCard}>
                    <div className={styles.cardBody}>
                      <div className={styles.productHeader}>
                        <div>
                          <h2 className={styles.productName}>{product.name}</h2>
                          <div className={styles.metaRow}>
                            <span className={styles.metaChip}>
                              {t('sku')}: {product.SKU}
                            </span>
                            <span className={styles.metaChip}>
                              {t('size')}: {product.size}
                            </span>
                            <span className={styles.metaChip}>
                              {t('totalQty')}: {productTotals.available}
                            </span>
                            <span className={styles.metaChip}>
                              {t('totalHeld')}: {productTotals.held}
                            </span>
                            <span className={styles.metaChip}>
                              {t('totalWithHold')}: {productTotals.totalWithHold}
                            </span>
                            {canViewPrices && (
                              <>
                                <span className={styles.valueChip}>
                                  {t('inventoryValue')}: {formatPrice(productTotals.availableValue)}
                                </span>
                                <span className={styles.valueChip}>
                                  {t('inventoryValueWithHold')}: {formatPrice(productTotals.totalWithHoldValue)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={styles.expandButton}
                          onClick={() => toggleProduct(product._id)}
                          aria-expanded={Boolean(expandedProducts[product._id])}
                        >
                          {expandedProducts[product._id] ? t('hideDetails') : t('showDetails')}
                        </button>
                      </div>

                      {expandedProducts[product._id] && (
                        <div className={styles.imageWrapper}>
                          <ProductImage
                            productId={product._id}
                            hasImage={Boolean(product.hasImage)}
                            alt={product.name}
                            className={styles.productImage}
                            placeholderClassName={styles.placeholderImage}
                            loadingLabel={t('loadingImage')}
                            noImageLabel={t('noImage')}
                            errorLabel={t('imageUnavailable')}
                          />
                        </div>
                      )}

                      {expandedProducts[product._id] && Array.isArray(product.sorts) && product.sorts.length > 0 && (
                        <div className={styles.sortList}>
                          <span className={styles.sortHeading}>{t('sortsAndColors')}</span>
                          {product.sorts.map((sortObj, idx) => {
                            const sortKey = `${product._id}-sort-${idx}`;
                            const sortExpanded = Boolean(expandedSorts[sortKey]);
                            const sortTotals = getSortTotals(sortObj);
                            const colors = Array.isArray(sortObj.colors) ? sortObj.colors : [];
                            return (
                              <div key={sortKey} className={styles.sortBlock}>
                                <div className={styles.sortHeader}>
                                  <button
                                    type="button"
                                    className={styles.sortToggleButton}
                                    onClick={() => toggleSort(sortKey)}
                                    aria-expanded={sortExpanded}
                                  >
                                    <span className={styles.sortName}>
                                      {t('sortLabel')}: {sortObj.sortName}
                                    </span>
                                    <span>{sortExpanded ? t('hideColors') : t('showColors')}</span>
                                  </button>
                                  <div className={styles.sortMetrics}>
                                    {canViewPrices && sortObj.price !== undefined && (
                                      <span className={styles.priceChip}>
                                        {t('price')}: {formatPrice(sortObj.price)}
                                      </span>
                                    )}
                                    <span className={styles.sortTotal}>
                                      {t('totalQty')}: {sortTotals.available}
                                    </span>
                                    <span className={styles.sortHeld}>
                                      {t('held')}: {sortTotals.held}
                                    </span>
                                    <span className={styles.sortGrandTotal}>
                                      {t('totalWithHold')}: {sortTotals.totalWithHold}
                                    </span>
                                    {canViewPrices && (
                                      <>
                                        <span className={styles.valueChip}>
                                          {t('availableValue')}: {formatPrice(sortTotals.availableValue)}
                                        </span>
                                        <span className={styles.valueChip}>
                                          {t('inventoryValueWithHold')}: {formatPrice(sortTotals.totalWithHoldValue)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                {sortExpanded && (
                                  <div className={styles.colorsGrid}>
                                    {colors.map((cObj, colorIdx) => (
                                      <div key={`${product._id}-sort-${idx}-color-${colorIdx}`} className={styles.colorCard}>
                                        {(() => {
                                          const quantity = Number(cObj.quantity) || 0;
                                          const held = Number(cObj.reservedQuantity) || 0;
                                          const price = Number(sortObj.price) || 0;
                                          return (
                                            <>
                                        <span className={styles.colorName}>{cObj.color}</span>
                                        <span className={styles.colorQty}>
                                          {t('available')}: {quantity}
                                        </span>
                                        <span className={styles.colorHeld}>
                                          {t('held')}: {held}
                                        </span>
                                        <span className={styles.colorTotal}>
                                          {t('totalWithHold')}: {quantity + held}
                                        </span>
                                        {canViewPrices && (
                                          <>
                                            <span className={styles.colorValue}>
                                              {t('availableValue')}: {formatPrice(quantity * price)}
                                            </span>
                                            <span className={styles.colorValue}>
                                              {t('inventoryValueWithHold')}: {formatPrice((quantity + held) * price)}
                                            </span>
                                          </>
                                        )}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>
          )}
        </>
      )}
    </AppLayout>
  );
};

export default ProductInventoryView;
